import DOMPurify from 'isomorphic-dompurify';
import { z, ZodError, ZodSchema } from 'zod';

// Sanitize HTML content
export const sanitizeHtml = (dirty: string): string => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target'],
  });
};

// Sanitize plain text (remove all HTML)
export const sanitizeText = (input: string): string => {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

// Email validation
export const emailSchema = z.string().email().max(255);

// Password validation
export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

// UUID validation
export const uuidSchema = z.string().uuid();

// Message content validation
export const messageSchema = z.object({
  content: z.string().min(1).max(10000),
  role: z.enum(['user', 'assistant']),
});

// Group name validation
export const groupNameSchema = z
  .string()
  .min(1)
  .max(100)
  .transform(sanitizeText);

// User profile validation
export const profileSchema = z.object({
  email: emailSchema,
  full_name: z.string().min(1).max(255).transform(sanitizeText).optional(),
  role: z.enum(['student', 'teacher', 'admin']),
});

// Prompt validation
export const promptSchema = z.object({
  title: z.string().min(1).max(255).transform(sanitizeText),
  content: z.string().min(1).max(5000),
  is_editable: z.boolean().default(false),
});

// Access session validation
export const accessSessionSchema = z
  .object({
    user_id: uuidSchema,
    group_id: uuidSchema,
    starts_at: z.string().datetime(),
    expires_at: z.string().datetime(),
  })
  .refine((data) => new Date(data.expires_at) > new Date(data.starts_at), {
    message: 'Expiry time must be after start time',
  });

// Example usage with error handling
export const validateMessage = (data: unknown) => {
  try {
    return messageSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid message format: ${error.issues[0].message}`);
    }
    throw new Error('Invalid message format');
  }
};

export const validateEmail = (email: unknown) => {
  try {
    return emailSchema.parse(email);
  } catch {
    throw new Error('Invalid email address');
  }
};

// Type exports
export type Message = z.infer<typeof messageSchema>;
export type Profile = z.infer<typeof profileSchema>;
export type Prompt = z.infer<typeof promptSchema>;
export type AccessSession = z.infer<typeof accessSessionSchema>;

// Validation utilities
export interface ValidationError {
  path: string;
  message: string;
}

/**
 * Validates data against a Zod schema and returns typed result
 */
export function validate<T>(
  schema: ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: ValidationError[] } {
  try {
    const validData = schema.parse(data);
    return { success: true, data: validData };
  } catch (error) {
    if (error instanceof ZodError) {
      return {
        success: false,
        errors: formatZodErrors(error),
      };
    }
    throw error;
  }
}

/**
 * Format Zod errors into a more user-friendly format
 */
export function formatZodErrors(error: ZodError): ValidationError[] {
  return error.issues.map((err) => ({
    path: err.path.join('.'),
    message: err.message,
  }));
}

/**
 * Create a validation middleware for API routes
 */
export function createValidator<T>(schema: ZodSchema<T>) {
  return (data: unknown): T => {
    const result = validate(schema, data);
    if (!result.success) {
      throw new Error(
        `Validation failed: ${result.errors.map((e) => `${e.path}: ${e.message}`).join(', ')}`
      );
    }
    return result.data;
  };
}

/**
 * Common validation patterns
 */
export const patterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  username: /^[a-zA-Z0-9_]{3,20}$/,
};
